import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Download,
  ExternalLink,
  FileText,
  Copy,
  Check,
  X,
  ChevronRight,
  Filter,
  Award,
  BookOpen,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/global/SEO';

interface ResearchIndexProps {
  lang: 'en' | 'ar';
}

interface ResearchPaper {
  id: string;
  titleEn: string;
  titleAr: string;
  category: 'foundation' | 'rag' | 'climate' | 'swarms' | 'safety';
  categoryLabelEn: string;
  categoryLabelAr: string;
  date: string;
  authorsEn: string;
  authorsAr: string;
  abstractEn: string;
  abstractAr: string;
  doi: string;
  researchGateUrl: string;
  pdfUrl: string;
  license: string;
  awardEn?: string;
  awardAr?: string;
  keyFindingsEn: string[];
  keyFindingsAr: string[];
  isSpotlight?: boolean;
}

const RESEARCH_PAPERS: ResearchPaper[] = [
  {
    id: 'droughtwatch-ai-v2-multivariate-2026',
    titleEn: 'DroughtWatch AI V2: A Multivariate Bidirectional LSTM Framework for Pixel-Level, Six-Month-Ahead Agricultural Drought Early Warning in the Faiyum Region, Egypt',
    titleAr: 'نظام DroughtWatch AI V2: إطار عمل متعدد المتغيرات لشبكات Bi-LSTM للتنبؤ بالجفاف الزراعي على مستوى البكسل قبل 6 أشهر في إقليم الفيوم، مصر',
    category: 'climate',
    categoryLabelEn: 'Climate & Drought AI',
    categoryLabelAr: 'ذكاء المناخ والجفاف',
    date: 'August 2026',
    authorsEn: 'Abdelrahman M. D. S. A. Abushousha',
    authorsAr: 'عبدالرحمن محمد دياب شوشة (Abdelrahman Abushousha)',
    abstractEn: 'Drought is a slow-onset hazard whose costs are compounded by the short lead time of most operational warning systems. This paper documents DroughtWatch AI V2, a multivariate, pixel-level system that forecasts six months of future Normalized Difference Vegetation Index (NDVI) independently across 25,522 individual cropland pixels (250 m resolution, ESA WorldCover-masked) in the Faiyum Governorate, Egypt, using a 300-month (2001–2025) multivariate satellite and reanalysis record — NDVI, EVI, land-surface temperature, precipitation, and root-zone soil moisture from MODIS, CHIRPS, and GLDAS. A stacked bidirectional Long Short-Term Memory (Bi-LSTM) network, trained on 12-month input sequences pooled across 1,500 randomly sampled pixels, was evaluated on a chronologically buffered (24-month gap), out-of-sample test set of 20,400 sequences against a per-pixel, calendar-month-matched Historical Seasonal Climatology baseline. The model reduced MAE and RMSE by approximately 28% relative to this baseline (MAE 0.0413 vs. 0.0572; RMSE 0.0536 vs. 0.0741) and explained 87.6% of out-of-sample variance versus 76.3% for the baseline (R²). The underlying project line was awarded Third Prize at the 2026 Global Environment Solutions Challenge (GESC), organized under the UNEP–Tongji Institute of Environment for Sustainable Development, among 574 teams from 58 countries.',
    abstractAr: 'يُعد الجفاف كارثة بطيئة الحدوث تتضاعف أضرارها الاقتصادية نتيجة قصر المدى الزمني للإنذار في النظم الحالية. توثق هذه الورقة البحثية نظام DroughtWatch AI V2، وهو نظام تنبؤي متعدد المتغيرات يعمل على مستوى البكسل الفردي للتنبؤ بمؤشر الغطاء النباتي (NDVI) لستة أشهر قادمة بشكل مستقل عبر 25,522 بكسل زراعي (بدقة 250 متراً وبقناع غطاء الأراضي ESA WorldCover) في محافظة الفيوم بمصر، بالاعتماد على سجل فضائي وبيانات إعادة التحليل المناخي تمتد لـ 300 شهر (2001-2025) تضم: NDVI و EVI وحرارة السطح (LST) والأمطار (CHIRPS) ورطوبة التربة الجذرية (GLDAS). تم تدريب شبكة Bi-LSTM متراكمة وتجربتها على 20,400 تسلسل اختباري مفصول بفاصل زمني صارم 24 شهراً لمنع تسرب البيانات. حقق النموذج تخفيضاً في الخطأ بنسبة 28% تقريباً (MAE 0.0413 و RMSE 0.0536) وفسّر 87.6% من التباين المستقبلي مقارنة بالمتوسط المناخي. حاز المشروع على الجائزة الثالثة عالمياً في تحدي الحلول البيئية العالمي (GESC 2026) تحت مظلة معهد البيئة للتنمية المستدامة المشترك بين برنامج الأمم المتحدة للبيئة (UNEP) وجامعة تونغجي، من بين 574 فريقاً من 58 دولة.',
    doi: '10.13140/RG.2.2.25563.53287',
    researchGateUrl: 'https://www.researchgate.net/publication/412631279_DroughtWatch_AI_V2_Multivariate_Bidirectional_LSTM_Framework_for_Pixel-Level_Agricultural_Drought_Early_Warning_in_Faiyum_Egypt',
    pdfUrl: '/research/papers/DroughtWatch_AI_V2_Paper.pdf',
    license: 'CC BY 4.0',
    awardEn: 'Global 3rd Prize Winner — 2026 Global Environment Solutions Challenge (UNEP–Tongji Institute, 574 Teams / 58 Countries)',
    awardAr: 'الفائز بالجائزة الثالثة عالمياً — تحدي الحلول البيئية العالمي GESC 2026 (معهد UNEP-Tongji، بين 574 فريقاً من 58 دولة)',
    keyFindingsEn: [
      'Pixel-level multivariate forecasting across 25,522 individual cropland pixels (250 m resolution, ESA WorldCover).',
      'Unified fusion of 5 satellite & reanalysis streams: MODIS NDVI, EVI, Land Surface Temp, CHIRPS precipitation, and NASA GLDAS root-zone soil moisture.',
      'Out-of-sample evaluation on 20,400 sequences with a 24-month buffer gap achieving MAE 0.0413, RMSE 0.0536, and R² 0.8762 (28% error reduction over per-pixel baseline).',
      'FastAPI deployment with ~180 ms deterministic latency and strict feature-ranges training domain boundary enforcement.'
    ],
    keyFindingsAr: [
      'تنبؤ مكاني متعدد المتغيرات على مستوى 25,522 بكسل زراعي بدقة 250 متراً باستخدام قناع ESA WorldCover.',
      'دمج 5 روافد بيئية وهيدرولوجية فضائية: MODIS NDVI، EVI، حرارة السطح، أمطار CHIRPS، ورطوبة التربة من NASA GLDAS.',
      'تقييم على 20,400 تسلسل اختباري بفاصل زمني 24 شهراً حقق MAE قدره 0.0413 و RMSE قدره 0.0536 و R² يبلغ 0.8762 (تخفيض 28% في الخطأ).',
      'نشر تشغيلي متكامل عبر واجهة FastAPI بزمن استجابة 180 مللي ثانية مع نظام حماية نطاق التدريب (Domain Boundaries).'
    ],
    isSpotlight: true,
  },
  {
    id: 'droughtwatch-ai-v1-bilstm-2026',
    titleEn: 'DroughtWatch AI: A Bidirectional LSTM Framework for Six-Month-Ahead NDVI Forecasting and Agricultural Drought Early Warning: A Proof-of-Concept Case Study from the Faiyum Region, Egypt',
    titleAr: 'نظام DroughtWatch AI: إطار عمل لشبكات LSTM ثنائية الاتجاه للتنبؤ بمؤشر الغطاء النباتي (NDVI) قبل 6 أشهر والإنذار المبكر بالجفاف الزراعي في محافظة الفيوم، مصر',
    category: 'climate',
    categoryLabelEn: 'Climate & Drought AI',
    categoryLabelAr: 'ذكاء المناخ والجفاف',
    date: 'July 2026',
    authorsEn: 'Abdelrahman M. D. S. A. Abushousha',
    authorsAr: 'عبدالرحمن محمد دياب شوشة (Abdelrahman Abushousha)',
    abstractEn: 'This paper documents the methodology, results, and structural validation of DroughtWatch AI, a framework that forecasts six months of future Normalized Difference Vegetation Index (NDVI) values from a 25-year (2000–2025) satellite-derived NDVI record for an agricultural zone in the Faiyum Governorate, Egypt. To ensure scientific rigor, we implement an airtight 24-step temporal buffer gap strictly between the training and testing datasets before sequence construction, completely eliminating data leakage from sliding window overlap. A stacked bidirectional Long Short-Term Memory (Bi-LSTM) network was trained on 12-month input sequences to output 6-month-ahead NDVI forecasts. The proposed Bi-LSTM model achieves absolute dominance over a Historical Seasonal Climatology baseline (which underperforms with an MAE of 0.0699, RMSE of 0.0834, and R² of -0.2580), reaching a definitive MAE of 0.0214 NDVI units, an RMSE of 0.0264, and a coefficient of determination (R²) of 0.8740 on the out-of-sample test set. In an illustrative retrospective case study, a forecast issued in September 2024 anticipated a decline in NDVI that closely matched independently obtained satellite observations from February 2025.',
    abstractAr: 'توثق هذه الورقة البحثية المنهجية والنتائج والتحقق الهيكلي لنظام DroughtWatch AI، وهو إطار عمل يتنبأ بقيم مؤشر الغطاء النباتي (NDVI) لستة أشهر قادمة بناءً على سجل فضائي يمتد لـ 25 عاماً (2000-2025) لمنطقة زراعية في محافظة الفيوم بمصر. لضمان الصرامة العلمية، تم تطبيق فاصل زمني صارم قدره 24 خطوة زمنية بين مجموعتي التدريب والاختبار قبل إنشاء التسلسلات لمنع أي تسرب للبيانات. تم تدريب شبكة Bi-LSTM متراكمة على نوافذ زمنية مدتها 12 شهراً لإصدار تنبؤات لـ 6 أشهر قادمة. حقق النموذج تفوقاً كاملاً على خط الأساس المناخي الموسمي محققاً خطأ MAE قدره 0.0214 و RMSE قدره 0.0264 و R² يبلغ 0.8740. وفي دراسة استرجاعية توضيحية، تنبأ النموذج في سبتمبر 2024 بانخفاض حاد في الغطاء النباتي تطابق بدقة مذهلة مع رصد الأقمار الصناعية الفعلي في فبراير 2025.',
    doi: '10.13140/RG.2.2.22395.89125',
    researchGateUrl: 'https://www.researchgate.net/publication/408839478_DroughtWatch_AI_Bidirectional_LSTM_Framework_for_Six-Month_Agricultural_Drought_Early_Warning_in_Faiyum_Egypt',
    pdfUrl: '/research/papers/DroughtWatch_AI_V1_Paper.pdf',
    license: 'CC BY 4.0',
    awardEn: 'Global 3rd Prize Winner — 2026 Global Environment Solutions Challenge (UNEP–Tongji Institute, 574 Teams / 58 Countries)',
    awardAr: 'الفائز بالجائزة الثالثة عالمياً — تحدي الحلول البيئية العالمي GESC 2026 (معهد UNEP-Tongji، بين 574 فريقاً من 58 دولة)',
    keyFindingsEn: [
      '6-month ahead NDVI forecasting with test-set MAE of 0.0214, RMSE of 0.0264, and R² of 0.8740 over 25-year satellite record (2000–2025).',
      '69.4% MAE reduction over Historical Seasonal Climatology baseline with an airtight 24-step chronological buffer gap eliminating data leakage.',
      'Monte Carlo Dropout uncertainty estimation achieving 87.2% probability early warning verified against Feb 2025 MODIS satellite observations.',
      'End-to-end FastAPI deployment with 184 ms inference latency for real-time agricultural decision support.'
    ],
    keyFindingsAr: [
      'التنبؤ بمؤشر NDVI قبل 6 أشهر بخطأ MAE قدره 0.0214 و RMSE قدره 0.0264 ومعامل تحديد R² يبلغ 0.8740 على مدار 25 عاماً من بيانات الأقمار الصناعية (2000-2025).',
      'تخفيض خطأ التنبؤ بنسبة 69.4% مقارنة بالمتوسط المناخي الموسمي مع عزل زمني صارم (24 خطوة) لمنع تسرب البيانات (Data Leakage).',
      'تقدير احتمالي لعدم اليقين عبر Monte Carlo Dropout بدقة إنذار مبكر بلغت 87.2% تم التحقق منها ميدانياً بصور أقمار MODIS لفبراير 2025.',
      'نشر تشغيلي متكامل عبر FastAPI بزمن استجابة 184 مللي ثانية لدعم اتخاذ القرار الزراعي في الوقت الفعلي.'
    ]
  }
];

export const ResearchIndex: React.FC<ResearchIndexProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [activePaperModal, setActivePaperModal] = useState<ResearchPaper | null>(null);
  const [copiedBibtexId, setCopiedBibtexId] = useState<string | null>(null);

  const categories = [
    { id: 'all', labelEn: 'All Publications', labelAr: 'جميع المنشورات' },
    { id: 'climate', labelEn: 'Climate & Drought AI', labelAr: 'ذكاء المناخ والجفاف' },
    { id: 'foundation', labelEn: 'Spatial Foundation Models', labelAr: 'نماذج الأساس المكانية' },
    { id: 'rag', labelEn: 'Spatial RAG', labelAr: 'الاستدلال المكاني' },
    { id: 'swarms', labelEn: 'Autonomous Agents', labelAr: 'الأنظمة الذاتية والوكلاء' },
    { id: 'safety', labelEn: 'Topological Safety', labelAr: 'الأمان الطوبولوجي' },
  ];

  const filteredPapers = useMemo(() => {
    return RESEARCH_PAPERS.filter((paper) => {
      const matchesCategory = selectedCategory === 'all' || paper.category === selectedCategory;
      const title = isAr ? paper.titleAr : paper.titleEn;
      const abstract = isAr ? paper.abstractAr : paper.abstractEn;
      const authors = isAr ? paper.authorsAr : paper.authorsEn;
      const q = searchQuery.toLowerCase().trim();

      const matchesSearch =
        !q ||
        title.toLowerCase().includes(q) ||
        abstract.toLowerCase().includes(q) ||
        authors.toLowerCase().includes(q) ||
        paper.doi.toLowerCase().includes(q);

      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory, isAr]);

  const handleCopyBibtex = (paper: ResearchPaper) => {
    const bibtex = `@article{abushousha_${paper.id.replace(/-/g, '_')},
  title={${paper.titleEn}},
  author={${paper.authorsEn}},
  journal={ResearchGate / VEXL Research Labs},
  year={2026},
  doi={${paper.doi}},
  url={${paper.researchGateUrl}}
}`;
    navigator.clipboard.writeText(bibtex);
    setCopiedBibtexId(paper.id);
    setTimeout(() => setCopiedBibtexId(null), 2500);
  };

  return (
    <div
      className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 ${isAr ? 'font-arabic' : 'font-sans'} antialiased`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <SEO
        title={isAr ? 'الأرشيف وفهرس الأبحاث العلمية | مختبرات فكسل' : 'Scientific Publications & Index | VEXL Research Labs'}
        description={isAr ? 'الأرشيف الأكاديمي الشامل للأوراق العلمية والمنشورات البحثية والتقارير الفنية الصادرة عن مختبرات فكسل.' : 'Explore peer-reviewed publications, technical reports, and preprints published by VEXL research teams.'}
        lang={lang}
      />

      {/* ── Top Header Section ────────────────────────────────────────────── */}
      <section className="pt-28 md:pt-32 pb-16 border-b border-[#262C31] relative">
        <div className="w-full max-w-[1450px] mx-auto px-6 relative z-10">

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-[#262C31]">
            <div className="space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold flex items-center gap-2">
                <BookOpen size={14} />
                <span>{isAr ? 'الأرشيف الأكاديمي وفهرس المنشورات' : 'PUBLICATIONS INDEX & ACADEMIC ARCHIVE'}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#EDF0F1]">
                {isAr ? 'الأبحاث والمنشورات العلمية' : 'Publications & Preprints'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/research/overview"
                className="px-4 py-2.5 rounded-sm border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/50 text-xs font-medium text-[#EDF0F1] transition-all flex items-center gap-1.5"
              >
                <span>{isAr ? 'الرؤية البحثية والمحاور' : 'Research Overview'}</span>
                <ChevronRight size={13} className={isAr ? 'rotate-180' : ''} />
              </Link>
              <Link
                to="/careers"
                className="px-4 py-2.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-semibold transition-all shadow-sm"
              >
                {isAr ? 'انضم للفريق العلمي' : 'Join the Lab'}
              </Link>
            </div>
          </div>

          <p className="text-base text-[#9AA5AC] max-w-3xl pt-6 leading-relaxed">
            {isAr
              ? 'فهرس شامل للأبحاث والتقارير العلمية والمنشورات المحكّمة المنشورة بواسطة مختبرات فكسل في مجالات الذكاء الاصطناعي الجغرافي، الاستدلال الطوبولوجي، ونماذج التنبؤ المناخي.'
              : 'A curated repository of peer-reviewed papers, preprints, and technical reports published by VEXL research teams across GeoAI, Spatial RAG, and ecohydrological forecasting.'}
          </p>

        </div>
      </section>

      {/* ── Publications Archive & Table Section ───────────────────────────── */}
      <section className="py-20 border-b border-[#262C31]">
        <div className="w-full max-w-[1450px] mx-auto px-6">

          {/* Search and Filter Controls */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] mb-2 font-semibold">
                {isAr ? 'تصفية وبحث الأرشيف' : 'SEARCH & FILTER ARCHIVE'}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'تصفح أوراق العمل المتاحة' : 'Browse Published Technical Papers'}
              </h2>
            </div>

            {/* Live Search Box */}
            <div className="relative w-full md:w-80">
              <Search size={15} className={`absolute top-1/2 -translate-y-1/2 text-[#9AA5AC] ${isAr ? 'right-3.5' : 'left-3.5'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isAr ? 'ابحث عن ورقة، مؤلف، أو معرف DOI...' : 'Search papers, authors, or DOI...'}
                className={`w-full py-2.5 rounded-sm border border-[#262C31] bg-[#12161A] text-xs text-[#EDF0F1] placeholder-[#626F79] focus:outline-none focus:border-[#0E93A3] focus:ring-1 focus:ring-[#0E93A3] transition-all ${
                  isAr ? 'pr-9 pl-4' : 'pl-9 pr-4'
                }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute top-1/2 -translate-y-1/2 text-[#9AA5AC] hover:text-[#EDF0F1] ${isAr ? 'left-3' : 'right-3'}`}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center gap-2 mb-8 pb-4 border-b border-[#262C31]">
            <span className="text-xs font-mono text-[#9AA5AC] mr-2 flex items-center gap-1">
              <Filter size={12} />
              <span>{isAr ? 'التصنيف:' : 'Domain:'}</span>
            </span>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-1.5 rounded-sm text-xs font-medium transition-all cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-[#0E93A3] text-white font-semibold shadow-sm'
                    : 'bg-[#12161A] text-[#9AA5AC] hover:text-[#EDF0F1] hover:bg-[#171C21] border border-[#262C31]'
                }`}
              >
                {isAr ? cat.labelAr : cat.labelEn}
              </button>
            ))}
          </div>

          {/* Publications Table (Anthropic / Academic Editorial Style) */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" dir={isAr ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="border-b border-[#262C31] text-[11px] font-mono text-[#9AA5AC] uppercase tracking-wider">
                  <th className="py-4 px-4 font-semibold w-32">{isAr ? 'التاريخ' : 'DATE'}</th>
                  <th className="py-4 px-4 font-semibold w-48">{isAr ? 'المجال' : 'DOMAIN'}</th>
                  <th className="py-4 px-4 font-semibold">{isAr ? 'عنوان الورقة البحثية والمستخلص' : 'TITLE & ABSTRACT'}</th>
                  <th className="py-4 px-4 font-semibold text-right w-44">{isAr ? 'الإجراءات والتحميل' : 'ACTIONS'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#262C31]">
                {filteredPapers.map((paper) => (
                  <tr
                    key={paper.id}
                    onClick={() => setActivePaperModal(paper)}
                    className="hover:bg-[#12161A]/60 transition-colors cursor-pointer group"
                  >
                    {/* Date & License */}
                    <td className="py-5 px-4 text-xs font-mono text-[#9AA5AC] align-top whitespace-nowrap">
                      <div className="font-semibold text-[#EDF0F1]">{paper.date}</div>
                      <div className="text-[10px] text-[#9AA5AC] mt-1">{paper.license}</div>
                    </td>

                    {/* Category Tag */}
                    <td className="py-5 px-4 align-top">
                      <span className="inline-block px-2.5 py-0.5 rounded-sm bg-[#171C21] border border-[#262C31] text-[#9AA5AC] text-[11px] font-mono">
                        {isAr ? paper.categoryLabelAr : paper.categoryLabelEn}
                      </span>
                    </td>

                    {/* Title & Abstract Preview */}
                    <td className="py-5 px-4 align-top space-y-2 max-w-2xl">
                      {paper.awardEn && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/20 text-[#D48207] text-[11px] font-semibold">
                          <Award size={12} />
                          <span>{isAr ? paper.awardAr : paper.awardEn}</span>
                        </div>
                      )}
                      <h4 className="text-sm font-bold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug">
                        {isAr ? paper.titleAr : paper.titleEn}
                      </h4>
                      <p className="text-xs text-[#9AA5AC] leading-relaxed line-clamp-2">
                        {isAr ? paper.abstractAr : paper.abstractEn}
                      </p>
                      <div className="text-[11px] font-mono text-[#9AA5AC]">
                        {isAr ? paper.authorsAr : paper.authorsEn} · <span className="text-[#9AA5AC]">DOI: {paper.doi}</span>
                      </div>
                    </td>

                    {/* Action Buttons */}
                    <td className="py-5 px-4 text-right align-top whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={paper.pdfUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          title={isAr ? 'تحميل ملف البحث (PDF)' : 'Download PDF'}
                          className="p-2 rounded-sm border border-[#262C31] bg-[#12161A] text-[#9AA5AC] hover:text-[#0E93A3] hover:border-[#0E93A3]/50 transition-colors inline-flex items-center justify-center cursor-pointer"
                        >
                          <Download size={13} />
                        </a>
                        <a
                          href={paper.researchGateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={isAr ? 'عرض على ResearchGate' : 'View on ResearchGate'}
                          className="p-2 rounded-sm border border-[#262C31] bg-[#12161A] text-[#9AA5AC] hover:text-[#EDF0F1] hover:border-[#0E93A3]/50 transition-colors inline-flex items-center justify-center cursor-pointer"
                        >
                          <ExternalLink size={13} />
                        </a>
                        <button
                          onClick={() => handleCopyBibtex(paper)}
                          title={isAr ? 'نسخ استشهاد BibTeX' : 'Copy BibTeX Citation'}
                          className="p-2 rounded-sm border border-[#262C31] bg-[#12161A] text-[#9AA5AC] hover:text-[#EDF0F1] hover:border-[#0E93A3]/50 transition-colors cursor-pointer"
                        >
                          {copiedBibtexId === paper.id ? <Check size={13} className="text-[#0E93A3]" /> : <Copy size={13} />}
                        </button>
                        <button
                          onClick={() => setActivePaperModal(paper)}
                          className="px-3 py-1.5 rounded-sm border border-[#0E93A3]/40 bg-[#0E93A3]/10 text-[#0E93A3] text-xs font-semibold hover:bg-[#0E93A3]/20 transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <span>{isAr ? 'عرض' : 'Read'}</span>
                          <ChevronRight size={12} className={isAr ? 'rotate-180' : ''} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPapers.length === 0 && (
              <div className="py-16 text-center text-[#9AA5AC] space-y-2">
                <FileText size={32} className="mx-auto text-[#626F79]" />
                <p className="text-sm">
                  {RESEARCH_PAPERS.length === 0
                    ? (isAr ? 'جاري تجهيز وفهرسة الأبحاث والمنشورات العلمية الرسمية.' : 'Official publications and technical reports are currently being indexed.')
                    : (isAr ? 'لم يتم العثور على أبحاث مطابقة لمعايير البحث.' : 'No research papers matched your search criteria.')}
                </p>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* ── Open Access & Data Availability Statement ───────────────────────── */}
      <section className="py-20 border-b border-[#262C31]">
        <div className="w-full max-w-[1450px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                [OPEN_ACCESS // CC_BY_4.0]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">
                {isAr ? 'الوصول المفتوح والشفافية' : 'Open Science & Access'}
              </h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'جميع الأوراق العلمية التمهيدية منشورة بترخيص المشاع الإبداعي CC BY 4.0 لتمكين الباحثين والجامعات من البناء عليها بحرية.'
                  : 'All technical reports and preprints are released under Creative Commons CC BY 4.0 for transparent academic citation and global reproducibility.'}
              </p>
            </div>

            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                [DATA_REPRODUCIBILITY]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">
                {isAr ? 'إتاحة البيانات والنماذج' : 'Artifacts & Reproducibility'}
              </h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'أوزان النماذج بصيغة Keras Native ومصفوفات الراستر متاحة للباحثين والمؤسسات الأكاديمية عند الطلب لأغراض التدقيق العلمي.'
                  : 'Native Keras model weights and multivariate raster grids are available upon academic request for benchmark replication and independent audits.'}
              </p>
            </div>

            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#4E9BB8] font-bold uppercase tracking-wider">
                [ACADEMIC_PEER_REVIEW]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">
                {isAr ? 'التحكيم والجوائز العالمية' : 'Global Benchmarking'}
              </h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'تم تتويج أبحاث مرصد الجفاف DroughtWatch بالجائزة الثالثة في تحدي GESC العالمي تحت مظلة معهد البيئة UNEP-Tongji.'
                  : 'Awarded Third Prize at the 2026 Global Environment Solutions Challenge (GESC) among 574 international teams from 58 countries.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Research Paper Details & Abstract Modal ──────────────────────────── */}
      <AnimatePresence>
        {activePaperModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-3xl rounded-lg border border-[#262C31] bg-[#12161A] p-6 sm:p-8 shadow-2xl text-[#EDF0F1] my-8 max-h-[90vh] overflow-y-auto relative"
            >
              {/* Close Button */}
              <button
                onClick={() => setActivePaperModal(null)}
                className="absolute top-6 right-6 p-2 rounded text-[#9AA5AC] hover:text-[#EDF0F1] hover:bg-[#171C21] transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>

              <div className="space-y-6">

                {/* Global Award Banner */}
                {activePaperModal.awardEn && (
                  <div className="p-3.5 rounded-sm bg-amber-500/10 border border-amber-500/30 flex items-center gap-3 text-[#D48207]">
                    <Award size={20} className="shrink-0" />
                    <div className="text-xs font-semibold">
                      {isAr ? activePaperModal.awardAr : activePaperModal.awardEn}
                    </div>
                  </div>
                )}

                {/* Meta Header */}
                <div className="flex flex-wrap items-center gap-3 pr-8">
                  <span className="px-3 py-1 rounded bg-[#0E93A3]/10 border border-[#0E93A3]/20 text-[#0E93A3] text-xs font-mono font-bold">
                    {isAr ? activePaperModal.categoryLabelAr : activePaperModal.categoryLabelEn}
                  </span>
                  <span className="text-xs font-mono text-[#9AA5AC]">{activePaperModal.date}</span>
                  <span className="text-xs font-mono text-[#9AA5AC]">License: {activePaperModal.license}</span>
                </div>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-bold text-[#EDF0F1] leading-snug">
                  {isAr ? activePaperModal.titleAr : activePaperModal.titleEn}
                </h3>

                {/* Authors & Institutional Affiliation */}
                <div className="text-xs font-mono text-[#9AA5AC] pt-2 border-t border-[#262C31] space-y-1">
                  <div>
                    <span className="text-[#626F79] font-bold uppercase tracking-wider">{isAr ? 'المؤلف الرئيسي:' : 'PRIMARY AUTHOR:'} </span>
                    <span className="text-[#EDF0F1] font-bold">{isAr ? activePaperModal.authorsAr : activePaperModal.authorsEn}</span>
                  </div>
                  <div className="text-[11px] text-[#9AA5AC]">
                    Philipps-Universität Marburg, Germany · Cairo University, Egypt · VEXL Research Labs
                  </div>
                </div>

                {/* Abstract */}
                <div className="space-y-2">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold">
                    {isAr ? 'المستخلص العلمي (Abstract)' : 'ABSTRACT'}
                  </h4>
                  <p className="text-sm text-[#9AA5AC] leading-relaxed p-4 rounded-sm bg-[#05070A] border border-[#262C31]">
                    {isAr ? activePaperModal.abstractAr : activePaperModal.abstractEn}
                  </p>
                </div>

                {/* Key Findings */}
                <div className="space-y-2">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold">
                    {isAr ? 'النتائج والمخرجات العلمية الرئيسية' : 'KEY SCIENTIFIC FINDINGS'}
                  </h4>
                  <ul className="space-y-2 text-xs text-[#9AA5AC]">
                    {(isAr ? activePaperModal.keyFindingsAr : activePaperModal.keyFindingsEn).map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] mt-1.5 shrink-0" />
                        <span>{finding}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Modal Footer: BibTeX & Actions */}
                <div className="pt-6 border-t border-[#262C31] flex flex-wrap items-center justify-between gap-4">
                  <button
                    onClick={() => handleCopyBibtex(activePaperModal)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] text-xs font-mono text-[#EDF0F1] hover:border-[#0E93A3]/50 transition-all cursor-pointer"
                  >
                    {copiedBibtexId === activePaperModal.id ? (
                      <>
                        <Check size={14} className="text-[#0E93A3]" />
                        <span className="text-[#0E93A3] font-bold">{isAr ? 'تم نسخ BibTeX بنجاح!' : 'BibTeX Copied!'}</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        <span>{isAr ? 'نسخ استشهاد BibTeX' : 'Copy BibTeX Citation'}</span>
                      </>
                    )}
                  </button>

                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={activePaperModal.pdfUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs shadow-sm transition-all cursor-pointer"
                    >
                      <Download size={13} />
                      <span>{isAr ? 'تحميل البحث (PDF)' : 'Download Paper (PDF)'}</span>
                    </a>
                    <a
                      href={activePaperModal.researchGateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] text-xs text-[#EDF0F1] hover:text-[#0E93A3] hover:border-[#0E93A3]/30 transition-all font-mono"
                    >
                      <span>ResearchGate</span>
                      <ExternalLink size={12} />
                    </a>
                    <a
                      href={`https://doi.org/${activePaperModal.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-[#9AA5AC] hover:text-[#0E93A3] transition-colors font-mono"
                    >
                      <span>DOI</span>
                      <ExternalLink size={12} />
                    </a>
                    <button
                      onClick={() => setActivePaperModal(null)}
                      className="px-5 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] hover:border-[#0E93A3]/40 text-xs font-medium text-[#EDF0F1] cursor-pointer"
                    >
                      {isAr ? 'إغلاق' : 'Close'}
                    </button>
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ResearchIndex;
